import { useState } from 'react';
import { Badge, Button, Csel, Icon, Input, Section, useToast } from '@efira/ui';

import { useRunAction } from '../../../api/queries';
import type { BindableAction, BindingsPayload } from '../../../api/queries';
import { label } from '../../../bindings/labels';

/**
 * «Тестовые действия»: выполнить действие игры прямо в кадре.
 *
 * ЭТО НЕ ТО ЖЕ, ЧТО ПРОГОН ПРИВЯЗКИ. Прогон подаёт СОБЫТИЕ и отвечает на
 * вопрос «сработает ли правило»; тестовое действие подаёт САМО ДЕЙСТВИЕ и
 * отвечает на вопрос «как оно выглядит в кадре». У эталона это две разные
 * секции колонки, и разделение верное: настраивая привязку, стример проверяет
 * правило, а настраивая игру - картинку.
 *
 * СЕКЦИЯ СВЁРНУТА ПО УМОЛЧАНИЮ - как у эталона: десять строк действий выше
 * настроек игры отодвинули бы настройки за пределы первого экрана.
 */
export function TestActions({ gameId, data }: { gameId: string; data: BindingsPayload }) {
  const actions = data.manifest.actions;

  if (actions.length === 0) {
    return null;
  }

  return (
    <Section title="Тестовые действия" id="test-actions" defaultOpen={false}>
      {/* Пояснения под заголовком нет: у эталона список начинается сразу, и
          два абзаца текста отодвигали первую строку на 67 px вместо 29. */}
      <ul className="flex flex-col gap-1">
        {actions.map((action) => (
          <TestActionRow key={action.id} gameId={gameId} action={action} />
        ))}
      </ul>
    </Section>
  );
}

/**
 * Строка действия: метка вида, название, шеврон параметров и запуск.
 *
 * ШЕВРОН БЕЗ ПОДПИСИ - у эталона так, и это единственное место, где мы
 * повторяем безымянную кнопку: у неё есть доступное имя и подсказка, а
 * подпись «Параметры» рядом с названием действия в колонке шириной 300 px
 * вытеснила бы само название.
 */
function TestActionRow({ gameId, action }: { gameId: string; action: BindableAction }) {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<Record<string, string | number>>(() => {
    const initial: Record<string, string | number> = {};

    for (const param of action.params) {
      if (param.default !== undefined) {
        initial[param.id] = param.default;
      }
    }

    return initial;
  });

  const run = useRunAction();
  const toast = useToast();
  const editable = action.params.filter((param) => param.type !== 'collection');

  return (
    <li data-test-action={action.id} className="flex flex-col gap-1">
      {/* Строка - такое же врезанное поле, как строки сведений об игре: у
          эталона у всех строк колонки одно оформление и одна высота. */}
      <div className="bg-muted/60 flex items-center gap-2 rounded px-2 py-1">
        {/* Вид действия цветной меткой, как у эталона: «за игрока» и
            «против игрока» различаются с одного взгляда. */}
        {/* Колонка метки фиксированной ширины: пилюли «за» и «против»
            разной ширины сдвигали названия на 28 px друг относительно
            друга в одном списке. */}
        <Badge
          variant={action.sign === 'negative' ? 'destructive' : 'default'}
          className="w-14 shrink-0 justify-center"
        >
          {action.sign === 'negative' ? 'против' : 'за'}
        </Badge>

        <span className="min-w-0 flex-1 truncate text-sm">{label(action.labelKey)}</span>

        {/* Колонка шеврона зарезервирована во ВСЕХ строках: у эталона правая
            группа «шеврон плюс кнопка» стоит на одной вертикали, а у нас
            шеврон то был, то нет, и кнопки разъезжались. У действия без
            параметров он неактивен и объясняет себя. */}
        <Button
          variant="secondary"
          size="s"
          className="px-1"
          data-test-chevron={action.id}
          aria-expanded={open}
          aria-label={`Параметры действия «${label(action.labelKey)}»`}
          title={editable.length === 0 ? 'У действия нет параметров' : 'Параметры действия'}
          disabled={editable.length === 0}
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          <Icon name="chevronDown" size="sm" className={open ? 'rotate-180' : ''} />
        </Button>

        {/* Не главная: главная кнопка консоли одна и снимает блокировку
            эфира; проверочных действий у игры до десятка. */}
        <Button
          variant="secondary"
          size="s"
          data-test-fire={action.id}
          loading={run.isPending}
          onClick={() => {
            run.mutate(
              { actionId: action.id, params },
              {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Действие ушло на оверлей' });
                },
                onError: (error) => {
                  const reason =
                    typeof error === 'object' && error !== null && 'reason' in error
                      ? String((error as { reason: unknown }).reason)
                      : 'Действие не выполнилось. Попробуйте ещё раз.';
                  toast.show({ tone: 'warning', title: 'Не выполнилось', text: reason });
                },
              },
            );
          }}
        >
          Запустить
        </Button>
      </div>

      {open && (
        <div data-test-params={action.id} className="ps-2">
          <div className="flex flex-col gap-2">
            {editable.map((param) => (
              <label key={param.id} className="flex flex-col gap-1 text-xs">
                <span className="text-foreground/80">{label(param.labelKey)}</span>

                {param.type === 'enum' ? (
                  <Csel
                    value={String(params[param.id] ?? param.values?.[0] ?? '')}
                    aria-label={label(param.labelKey)}
                    triggerClassName="w-full"
                    options={(param.values ?? []).map((value) => ({
                      value,
                      label: label(`${param.labelKey}.${value}`),
                    }))}
                    onChange={(next) => {
                      setParams((current) => ({ ...current, [param.id]: next }));
                    }}
                  />
                ) : (
                  <Input
                    type={param.type === 'number' ? 'number' : 'text'}
                    min={param.min}
                    max={param.max}
                    value={String(params[param.id] ?? '')}
                    onChange={(event) => {
                      const raw = event.target.value;
                      setParams((current) => ({
                        ...current,
                        [param.id]: param.type === 'number' ? Number(raw) : raw,
                      }));
                    }}
                  />
                )}

                {/* Предел объявлен ЧИСЛОМ рядом с полем - правило видимости 3. */}
                {param.type === 'number' &&
                  (param.min !== undefined || param.max !== undefined) && (
                    <span className="text-foreground/80">
                      допустимо от {param.min ?? 0} до {param.max ?? '∞'}
                    </span>
                  )}
              </label>
            ))}
          </div>

          <p className="text-foreground/80 mt-1 text-sm">Только для этого запуска.</p>
        </div>
      )}

      {/* Идентификатор игры нужен запросу, но не глазу: он в адресе запроса. */}
      <span hidden data-test-game={gameId} />
    </li>
  );
}
